import React, { useState, useRef, useEffect } from "react";
import './seeking-overlay.scss';
import { formatTime } from "../../util";
import StandardButton from "../buttons/standard-button";

interface SeekingOverlayProps {
  getCurrentTime: () => number;
  videoDuration: number;
  isPlaying: boolean;
  onSeek: (newSeekFraction: number) => void;
  onPauseOrPlay: (newIsPaused: boolean) => void;
}

/**
 * A component that renders a seeking overlay for a video player.
 * It shows two lines with time labels: one for the current seek position and one for the hovered seek position.
 * The current position line has a pause/play button which triggers onPauseOrPlay when clicked.
 * The user can click anywhere else on the overlay to change the seek position. 
 * @param {() => number} getCurrentTime - function that returns current time
 * @param {number} videoDuration - duration of the current video
 * @param {boolean} isPlaying - tells whether the video is paused or playing
 * @param {(newSeekFraction: number) => void} onSeek - The function to apply the new seek position to the playing video.
 * @param {(newIsPaused: boolean) => void} onPauseOrPlay - The function that runs when the pause/play button is clicked, returns true if pause is clicked
 */
const SeekingOverlay: React.FC<SeekingOverlayProps> = ({getCurrentTime, videoDuration, isPlaying, onSeek, onPauseOrPlay}) => {
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [pauseOrPlayHovered, setPauseOrPlayHovered] = useState<boolean>(false);
  const [overlayIsShowing, setOverlayIsShowing] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  /**
   * Calculates the seek fraction from a given x-coordinate within the overlay.
   * @param {number} x - The x-coordinate of the event.
   * @returns {number} - The seek fraction, between 0 and 1.
   */
  function getSeekFractionFromX(x: number): number {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
    }
    return 0;
  };

  /**
   * Handles the mouse or touch move event to update the hoverX coordinate.
   * @param {React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>} e - The mouse or touch event object.
   */
  function handleMouseMove(e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): void {
    setHoverX('clientX' in e ? e.clientX : e.touches[0].clientX);
  };

  /**
   * Handles the click or touch event when the user clicks on the overlay to change the seek position.
   */
  function handleClick(): void {
    if (hoverX !== null) onSeek(getSeekFractionFromX(hoverX));
  };

  /**
   * Handles the touch end event to show the overlay if it is hidden, or handle it as a click if the overlay is showing
   */
  function handleTouchEnd(): void {
    if (overlayIsShowing) {
      handleClick();
    } else {
      setHoverX(null);
      setOverlayIsShowing(true);
    }
  }

  //Update the current time every 100 milliseconds
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    interval = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 100);
    // Clear the interval when the component unmounts
    return () => {
      if (interval !== null) clearInterval(interval);
    }
  }, []);


  return (
    <div
      className={`video-seek-overlay ${overlayIsShowing && 'showing'}`}
      ref={containerRef}
      onMouseEnter={()=>{setOverlayIsShowing(true)}}
      onMouseLeave={()=>{setOverlayIsShowing(false)}}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      onTouchStart={handleMouseMove}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleTouchEnd}>
      {!pauseOrPlayHovered &&
      <div className="line seeking" style={{left: `${getSeekFractionFromX(hoverX !== null ? hoverX : 0) * 100}%`}}>
        <div className="time-label">
          {formatTime(getSeekFractionFromX(hoverX !== null ? hoverX : 0) * videoDuration * 1000, true)}
        </div>
      </div>
      }
      <div className="line current" style={{left: `${currentTime / videoDuration * 100}%`}}>
        <div 
            className={`play-pause-button ${isPlaying ? 'playing' : 'paused'}`} 
            onMouseEnter={()=>{setPauseOrPlayHovered(true)}}
            onMouseLeave={()=>{setPauseOrPlayHovered(false)}}
            onTouchEnd={(e)=>{e.stopPropagation()}}>
          <StandardButton 
            classNames="round primary" 
            buttonText={isPlaying ? `||` : `▶`} 
            clickHandler={()=>{onPauseOrPlay(!isPlaying)}}/>
        </div>
        <div className="time-label">
          {formatTime(currentTime * 1000, true)}
        </div>
      </div>
    </div>
  );
};

export default SeekingOverlay;
